import React, {useState, useEffect} from "react";
import {useAuth} from "../../AuthContext";
import {useNavigate} from "react-router-dom";
import {getDisplayTime} from "../../utils/dateUtils";

const EmailVerification: React.FC = () => {
  const [isChecking, setIsChecking] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [waitingForAuth, setWaitingForAuth] = useState(false);
  const {
    user,
    loading,
    resendEmailVerification,
    refreshVerificationStatus,
    error,
    clearError,
  } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const justRegistered = localStorage.getItem("justRegistered") === "true";

    // Wait for auth state to settle before making redirect decisions
    if (loading) {
      return; // Still loading, don't make any navigation decisions yet
    }

    if (!user) {
      if (justRegistered && !waitingForAuth) {
        // User just registered, give more time for auth state to settle
        setWaitingForAuth(true);

        // Wait longer for newly registered users (production can be slow)
        setTimeout(() => {
          setWaitingForAuth(false);
          if (!user) {
            // Still no user after extended wait - redirect to login
            localStorage.removeItem("justRegistered");
            localStorage.removeItem("registrationEmail");
            navigate("/auth/login");
          }
        }, 5000); // Wait 5 seconds for auth state

        return;
      } else if (!waitingForAuth) {
        // Not a fresh registration and auth is done loading - redirect to login
        navigate("/auth/login");
        return;
      }
      // If waitingForAuth is true, just return and wait
      return;
    }

    // User is loaded - clear registration flags and check verification
    localStorage.removeItem("justRegistered");
    localStorage.removeItem("registrationEmail");
    setWaitingForAuth(false);

    // Check verification status on mount
    checkVerificationStatus();

    // Also set up an interval to check verification status every 10 seconds
    const interval = setInterval(() => {
      if (user && !isVerified) {
        checkVerificationStatus();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [user, loading, navigate, waitingForAuth, isVerified]);

  const checkVerificationStatus = async () => {
    if (!user) return;

    setIsChecking(true);
    clearError();

    try {
      const verified = await refreshVerificationStatus();
      setIsVerified(verified);
      setLastChecked(new Date());

      if (verified) {
        // Redirect to overview after a short delay
        setTimeout(() => {
          navigate("/overview");
        }, 2000);
      }
    } catch (error) {
      // Error is handled by the auth hook
    } finally {
      setIsChecking(false);
    }
  };

  const handleResendEmail = async () => {
    if (!user) return;

    setIsResending(true);
    clearError();

    try {
      await resendEmailVerification();
      setResendMessage("Verification email sent! Please check your inbox.");
      // Clear message after 5 seconds
      setTimeout(() => setResendMessage(null), 5000);
    } catch (error) {
      // Error is handled by the auth hook
    } finally {
      setIsResending(false);
    }
  };

  const handleContinue = () => {
    navigate("/overview");
  };

  // Show loading state while auth is settling
  if (loading || waitingForAuth) {
    const isWaitingForRegistration =
      localStorage.getItem("justRegistered") === "true";

    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1>
            {isWaitingForRegistration
              ? "Setting up your account..."
              : "Loading..."}
          </h1>
          <p>
            {isWaitingForRegistration
              ? "Please wait while we set up your verification page. This may take a few moments in production."
              : "Setting up your verification page..."}
          </p>
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
          {isWaitingForRegistration && (
            <p style={{marginTop: "16px", fontSize: "14px", color: "#666"}}>
              If this takes longer than expected, try refreshing the page.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>Verify Your Email</h1>

        {isVerified ? (
          <div className="verification-success">
            <div className="success-icon">✓</div>
            <h2>Email Verified!</h2>
            <p>Your email address has been successfully verified.</p>
            <p>Redirecting you to the dashboard...</p>
            <button className="btn btn-primary" onClick={handleContinue}>
              Continue to Dashboard
            </button>
          </div>
        ) : (
          <div className="verification-pending">
            <div className="email-icon">📧</div>
            <h2>Check Your Email</h2>
            <p>
              We've sent a verification email to <strong>{user.email}</strong>
            </p>
            <p>
              Please check your inbox and click the verification link to
              activate your account. The verification link will automatically
              verify your account when clicked.
            </p>

            <div className="verification-actions">
              <button
                className="btn btn-primary"
                onClick={checkVerificationStatus}
                disabled={isChecking}
              >
                {isChecking ? "Checking..." : "I've Verified My Email"}
              </button>

              <button
                className="btn btn-secondary"
                onClick={handleResendEmail}
                disabled={isResending}
              >
                {isResending ? "Sending..." : "Resend Verification Email"}
              </button>
            </div>

            {lastChecked && (
              <p className="last-checked">
                Last checked: {getDisplayTime(lastChecked)}
              </p>
            )}

            {resendMessage && (
              <div
                className="success-message"
                style={{
                  backgroundColor: "#d4edda",
                  color: "#155724",
                  padding: "12px",
                  borderRadius: "4px",
                  marginTop: "16px",
                  border: "1px solid #c3e6cb",
                }}
              >
                {resendMessage}
              </div>
            )}

            <div className="verification-tips">
              <h3>Can't find the email?</h3>
              <ul>
                <li>Check your spam or junk folder</li>
                <li>Make sure you entered the correct email address</li>
                <li>Wait a few minutes for the email to arrive</li>
                <li>Try clicking "Resend Verification Email" above</li>
                <li>Our emails come from MailerSend with custom branding</li>
                <li>You can only request 3 verification emails per hour</li>
              </ul>
            </div>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}
      </div>
    </div>
  );
};

export default EmailVerification;
